import { createContext, lazy, useState, useCallback, useEffect, useMemo, useContext, useId, Suspense, Component, useRef } from 'react';
import { Box, Container, Stack, Typography, Card, CardContent, TextField, InputAdornment, IconButton, FormHelperText, CircularProgress, Button, Drawer, AppBar, Toolbar, Avatar, Menu, MenuItem, Tooltip, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useIntl, FormattedMessage, IntlProvider as IntlProvider$1 } from 'react-intl';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { useQueryClient } from '@tanstack/react-query';
import { getCurrentUser, fetchUserAttributes, signIn, confirmSignIn, signUp, fetchAuthSession, confirmSignUp, resendSignUpCode, resetPassword, confirmResetPassword, updatePassword, signOut, fetchMFAPreference, setUpTOTP, verifyTOTPSetup, updateMFAPreference } from 'aws-amplify/auth';
import { decodeJwt } from 'jose';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useLocation, Navigate, useNavigate, Outlet, NavLink } from 'react-router';
import MenuIcon from '@mui/icons-material/Menu';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { QRCodeSVG } from 'qrcode.react';

var __defProp = Object.defineProperty;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __esm = (fn, res) => function __init() {
  return fn && (res = (0, fn[__getOwnPropNames(fn)[0]])(fn = 0)), res;
};
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/components/PasswordStrengthMeter.tsx
var PasswordStrengthMeter_exports = {};
__export(PasswordStrengthMeter_exports, {
  default: () => PasswordStrengthMeter_default
});
function loadZxcvbn() {
  if (zxcvbnPromise) return zxcvbnPromise;
  zxcvbnPromise = (async () => {
    const [coreMod, commonMod, enMod] = await Promise.all([
      import('@zxcvbn-ts/core'),
      import('@zxcvbn-ts/language-common'),
      import('@zxcvbn-ts/language-en')
    ]);
    coreMod.zxcvbnOptions.setOptions({
      translations: enMod.translations,
      graphs: commonMod.adjacencyGraphs,
      dictionary: {
        ...commonMod.dictionary,
        ...enMod.dictionary
      }
    });
    return coreMod.zxcvbnAsync;
  })();
  return zxcvbnPromise;
}
function scoreToColor(score) {
  if (score <= 1) return "error.main";
  if (score === 2) return "warning.main";
  return "success.main";
}
function scoreToLabelKey(score) {
  switch (score) {
    case 0:
      return "password.strengthVeryWeak";
    case 1:
      return "password.strengthWeak";
    case 2:
      return "password.strengthFair";
    case 3:
      return "password.strengthGood";
    case 4:
      return "password.strengthStrong";
  }
}
function PasswordStrengthMeter({
  value,
  onStrengthChange,
  id
}) {
  const [score, setScore] = useState(null);
  useEffect(() => {
    if (!value) {
      setScore(null);
      return;
    }
    let cancelled = false;
    void loadZxcvbn().then(
      (scorer) => scorer(value).then((result) => {
        if (cancelled) return;
        const next = result.score;
        setScore((prev) => {
          if (prev !== next && onStrengthChange) onStrengthChange(next);
          return next;
        });
      })
    );
    return () => {
      cancelled = true;
    };
  }, [value, onStrengthChange]);
  if (!value || score === null) return null;
  const barColor = scoreToColor(score);
  return /* @__PURE__ */ jsxs(
    Box,
    {
      id,
      role: "meter",
      "aria-valuemin": 0,
      "aria-valuemax": 4,
      "aria-valuenow": score,
      "aria-label": "Password strength",
      sx: { mt: 0.5 },
      children: [
        /* @__PURE__ */ jsx(Box, { sx: { display: "flex", gap: 0.5 }, "aria-hidden": "true", children: [0, 1, 2, 3].map((i) => /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              flex: 1,
              height: 4,
              borderRadius: 1,
              bgcolor: i < score ? barColor : "action.disabledBackground",
              transition: "background-color 120ms ease"
            }
          },
          i
        )) }),
        /* @__PURE__ */ jsxs(
          Typography,
          {
            variant: "caption",
            component: "div",
            sx: { mt: 0.25, color: barColor, fontWeight: 500 },
            children: [
              /* @__PURE__ */ jsx(FormattedMessage, { id: "password.strengthLabel" }),
              ": ",
              /* @__PURE__ */ jsx(FormattedMessage, { id: scoreToLabelKey(score) })
            ]
          }
        )
      ]
    }
  );
}
var zxcvbnPromise, PasswordStrengthMeter_default;
var init_PasswordStrengthMeter = __esm({
  "src/components/PasswordStrengthMeter.tsx"() {
    zxcvbnPromise = null;
    PasswordStrengthMeter_default = PasswordStrengthMeter;
  }
});
var AuthContext = createContext(null);

// src/auth/vectrosApiTokenCache.ts
var REFRESH_BEFORE_MS = 6e4;
var cachedTokens = /* @__PURE__ */ new Map();
var cachedExpiriesMs = /* @__PURE__ */ new Map();
var inFlightMints = /* @__PURE__ */ new Map();
var cacheGeneration = 0;
var minter = null;
function slotKey(tenantId, contextId) {
  return `${tenantId}|${contextId ?? ""}`;
}
function setPartnerApiTokenMinter(source) {
  minter = source;
}
function getVectrosApiToken(tenantId, contextId) {
  if (!tenantId) {
    return Promise.reject(new Error("getVectrosApiToken: a tenantId is required"));
  }
  const key = slotKey(tenantId, contextId);
  const now = Date.now();
  const cachedToken = cachedTokens.get(key);
  if (cachedToken && (cachedExpiriesMs.get(key) ?? 0) > now + REFRESH_BEFORE_MS) {
    return Promise.resolve(cachedToken);
  }
  const inFlight = inFlightMints.get(key);
  if (inFlight) {
    return inFlight;
  }
  const generationAtStart = cacheGeneration;
  let mintPromise;
  mintPromise = (async () => {
    try {
      if (!minter) {
        throw new Error(
          "vectrosApiTokenCache: partner-API token minter not registered. Call setPartnerApiTokenMinter() at app boot before any partner-API call."
        );
      }
      const { token, expiresAtMs } = await minter(tenantId, contextId);
      if (cacheGeneration !== generationAtStart) {
        throw new Error("Session cleared during partner-API token mint; discarding result.");
      }
      cachedTokens.set(key, token);
      cachedExpiriesMs.set(key, expiresAtMs);
      return token;
    } finally {
      if (inFlightMints.get(key) === mintPromise) {
        inFlightMints.delete(key);
      }
    }
  })();
  inFlightMints.set(key, mintPromise);
  return mintPromise;
}
function clearVectrosApiTokenCache() {
  cacheGeneration += 1;
  cachedTokens.clear();
  cachedExpiriesMs.clear();
  inFlightMints.clear();
}
function __resetVectrosApiTokenCacheForTest() {
  clearVectrosApiTokenCache();
  minter = null;
}
function AuthProvider({ provider, children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshUser = useCallback(async () => {
    const current = await provider.getCurrentUser();
    setUser(current);
  }, [provider]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const current = await provider.getCurrentUser();
        if (!cancelled) setUser(current);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [provider]);
  const signIn = useCallback(
    async (input) => {
      const result = await provider.signIn(input);
      if (result.kind === "COMPLETE") {
        await refreshUser();
      }
      return result;
    },
    [provider, refreshUser]
  );
  const confirmSignIn = useCallback(
    async (input) => {
      const result = await provider.confirmSignIn(input);
      if (result.kind === "COMPLETE") {
        await refreshUser();
      }
      return result;
    },
    [provider, refreshUser]
  );
  const signUp = useCallback(
    (input) => provider.signUp(input),
    [provider]
  );
  const confirmSignUp = useCallback(
    (input) => provider.confirmSignUp(input),
    [provider]
  );
  const resendSignUpCode = useCallback(
    (input) => provider.resendSignUpCode(input),
    [provider]
  );
  const forgotPassword = useCallback(
    (input) => provider.forgotPassword(input),
    [provider]
  );
  const confirmForgotPassword = useCallback(
    (input) => provider.confirmForgotPassword(input),
    [provider]
  );
  const changePassword = useCallback(
    (input) => provider.changePassword(input),
    [provider]
  );
  const signOut = useCallback(async () => {
    await provider.signOut();
    clearVectrosApiTokenCache();
    setUser(null);
  }, [provider]);
  const getIdToken = useCallback(() => provider.getIdToken(), [provider]);
  const getMemberships = useCallback(() => provider.getMemberships(), [provider]);
  const getActiveTenant = useCallback(() => provider.getActiveTenant(), [provider]);
  const getActivePartnerUserId = useCallback(
    () => provider.getActivePartnerUserId(),
    [provider]
  );
  const setActiveTenant = useCallback(
    (tenantId) => provider.setActiveTenant(tenantId),
    [provider]
  );
  const checkUserExists = useCallback(
    (email) => provider.checkUserExists(email),
    [provider]
  );
  const linkInvitation = useCallback(
    (inviteToken) => provider.linkInvitation(inviteToken),
    [provider]
  );
  const listAppContexts = useCallback(
    (tenantId, options) => provider.listAppContexts ? provider.listAppContexts(tenantId, options) : Promise.resolve([]),
    [provider]
  );
  const getMfaStatus = useCallback(() => provider.getMfaStatus(), [provider]);
  const setUpTotp = useCallback(() => provider.setUpTotp(), [provider]);
  const verifyTotpSetup = useCallback(
    (code) => provider.verifyTotpSetup(code),
    [provider]
  );
  const disableTotp = useCallback(() => provider.disableTotp(), [provider]);
  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: user !== null,
      signIn,
      confirmSignIn,
      signUp,
      confirmSignUp,
      resendSignUpCode,
      forgotPassword,
      confirmForgotPassword,
      changePassword,
      signOut,
      getIdToken,
      getMemberships,
      getActiveTenant,
      getActivePartnerUserId,
      setActiveTenant,
      checkUserExists,
      linkInvitation,
      listAppContexts,
      getMfaStatus,
      setUpTotp,
      verifyTotpSetup,
      disableTotp
    }),
    [
      user,
      loading,
      signIn,
      confirmSignIn,
      signUp,
      confirmSignUp,
      resendSignUpCode,
      forgotPassword,
      confirmForgotPassword,
      changePassword,
      signOut,
      getIdToken,
      getMemberships,
      getActiveTenant,
      getActivePartnerUserId,
      setActiveTenant,
      checkUserExists,
      linkInvitation,
      listAppContexts,
      getMfaStatus,
      setUpTotp,
      verifyTotpSetup,
      disableTotp
    ]
  );
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value, children });
}
function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

// src/auth/errors.ts
var AuthError = class _AuthError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
    Object.setPrototypeOf(this, _AuthError.prototype);
  }
  code;
  name = "AuthError";
};
function isAuthError(e) {
  return e instanceof AuthError;
}

// src/auth/errorMessages.ts
function authErrorToMessage(intl, e) {
  if (e instanceof AuthError) {
    if (e.code === "PASSWORD_POLICY_VIOLATION" && e.message) return e.message;
    return intl.formatMessage({ id: `auth.errors.${e.code}` });
  }
  return intl.formatMessage({ id: "auth.errors.UNKNOWN" });
}
var CurrentTenantContext = createContext(null);
function useCurrentTenant() {
  const ctx = useContext(CurrentTenantContext);
  if (ctx) return ctx;
  return {
    tenant: null,
    setTenant: async () => void 0,
    memberships: [],
    loading: false,
    activeMembership: null
  };
}
function useActiveTenantId() {
  const { tenant } = useCurrentTenant();
  if (tenant == null) {
    throw new Error(
      "useActiveTenantId: no active tenant. Tenant-scoped pages must render inside the AppLayout tenant gate (or, in tests, a CurrentTenantProvider seeded with initialTenant)."
    );
  }
  return tenant;
}

// src/auth/useScopeGate.ts
var decodedByToken = /* @__PURE__ */ new Map();
function decodeAllowedActions(token) {
  const cached = decodedByToken.get(token);
  if (cached) return cached;
  const empty = [];
  const stripped = token.replace(/^st_(live|test)_/, "");
  const parts = stripped.split(".");
  if (parts.length !== 3) {
    decodedByToken.set(token, empty);
    return empty;
  }
  try {
    const payloadSegment = parts[1] ?? "";
    const standard = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const padding = (4 - standard.length % 4) % 4;
    const padded = standard + "=".repeat(padding);
    const json = atob(padded);
    const claims = JSON.parse(json);
    const clauses = claims.scope?.scopes;
    if (!Array.isArray(clauses)) {
      decodedByToken.set(token, empty);
      return empty;
    }
    const actions = /* @__PURE__ */ new Set();
    for (const clause of clauses) {
      const list = clause?.allowed_actions;
      if (Array.isArray(list)) {
        for (const a of list) if (typeof a === "string") actions.add(a);
      }
    }
    const result = [...actions];
    decodedByToken.set(token, result);
    return result;
  } catch {
    decodedByToken.set(token, empty);
    return empty;
  }
}
function useScopeGate(tenantOverride) {
  const { tenant } = useCurrentTenant();
  const tenantId = tenantOverride ?? tenant;
  const [actions, setActions] = useState(null);
  useEffect(() => {
    if (tenantId == null) return;
    let cancelled = false;
    setActions(null);
    getVectrosApiToken(tenantId).then((token) => {
      if (!cancelled) setActions(decodeAllowedActions(token));
    }).catch(() => {
      if (!cancelled) setActions([]);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);
  const allowed = actions ?? [];
  const can = (action) => {
    if (allowed.includes("*")) return true;
    return allowed.includes(action);
  };
  return {
    loading: actions === null,
    allowedActions: allowed,
    can
  };
}
function __resetScopeGateDecodeCacheForTest() {
  decodedByToken.clear();
}
function ScopeGate({
  action,
  fallback = null,
  children
}) {
  const gate = useScopeGate();
  if (gate.loading) return /* @__PURE__ */ jsx(Fragment, { children: null });
  return /* @__PURE__ */ jsx(Fragment, { children: gate.can(action) ? children : fallback });
}
function CurrentTenantProvider({
  children,
  initialMemberships,
  initialTenant
}) {
  const { user, getMemberships, getActiveTenant, setActiveTenant } = useAuth();
  const queryClient = useQueryClient();
  const seeded = initialMemberships !== void 0;
  const [memberships, setMemberships] = useState(
    initialMemberships ?? []
  );
  const [tenant, setTenantState] = useState(initialTenant ?? null);
  const [loading, setLoading] = useState(!seeded);
  const userSub = user?.sub ?? null;
  useEffect(() => {
    if (seeded) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const [list, active] = await Promise.all([getMemberships(), getActiveTenant()]);
        if (cancelled) return;
        setMemberships(list);
        const resolved = active && list.some((m) => m.tenantId === active) ? active : list[0]?.tenantId ?? null;
        setTenantState(resolved);
      } catch {
        if (!cancelled) {
          setMemberships([]);
          setTenantState(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [seeded, userSub, getMemberships, getActiveTenant]);
  const setTenant = useCallback(
    async (next) => {
      await setActiveTenant(next);
      clearVectrosApiTokenCache();
      setTenantState(next);
      await queryClient.invalidateQueries();
    },
    [setActiveTenant, queryClient]
  );
  const value = useMemo(
    () => ({
      tenant,
      setTenant,
      memberships,
      loading,
      activeMembership: memberships.find((m) => m.tenantId === tenant) ?? null
    }),
    [tenant, setTenant, memberships, loading]
  );
  return /* @__PURE__ */ jsx(CurrentTenantContext.Provider, { value, children });
}
function mapAmplifyError(e) {
  if (e instanceof AuthError) return e;
  if (!(e instanceof Error)) {
    return new AuthError("UNKNOWN", "An unexpected error occurred.");
  }
  switch (e.name) {
    case "NotAuthorizedException":
    case "UserNotFoundException":
      return new AuthError("INVALID_CREDENTIALS", e.message);
    case "UserNotConfirmedException":
      return new AuthError("USER_NOT_CONFIRMED", e.message);
    case "CodeMismatchException":
      return new AuthError("CODE_MISMATCH", e.message);
    case "ExpiredCodeException":
      return new AuthError("EXPIRED_CODE", e.message);
    case "InvalidPasswordException":
      return new AuthError("PASSWORD_POLICY_VIOLATION", e.message);
    case "LimitExceededException":
    case "TooManyRequestsException":
    case "TooManyFailedAttemptsException":
      return new AuthError("LIMIT_EXCEEDED", e.message);
    case "NetworkError":
      return new AuthError("NETWORK_ERROR", e.message);
    default:
      return new AuthError("UNKNOWN", e.message);
  }
}
async function tryAmplify(fn) {
  try {
    return await fn();
  } catch (e) {
    throw mapAmplifyError(e);
  }
}
function isLingeringSessionError(e) {
  if (!(e instanceof Error)) return false;
  if (e.name === "UserAlreadyAuthenticatedException") return true;
  return e.name === "NotAuthorizedException" && /revoked/i.test(e.message);
}
function mapCognitoMfaType(type) {
  switch (type) {
    case "TOTP":
      return "TOTP";
    case "SMS":
      return "SMS";
    case "EMAIL":
      return "EMAIL";
    default:
      return null;
  }
}
var CognitoAuthProvider = class {
  /**
   * Deployment-specific configuration injected by the host app — see
   * {@link CognitoAuthProviderConfig}. To swap Cognito for another provider in a
   * fork, implement {@link AuthProviderAdapter} directly instead of constructing
   * this class.
   */
  constructor(config) {
    this.config = config;
  }
  config;
  /**
   * Memoized memberships, used only to resolve a tenantId → its env mode for
   * {@link mintPartnerApiToken}. Stable for a session (a tenant's env mode
   * doesn't change); cleared on signOut so a new identity never reads the
   * previous user's mapping.
   */
  cachedMemberships = null;
  async getCurrentUser() {
    try {
      await getCurrentUser();
      const attrs = await fetchUserAttributes();
      return this.attributesToUser(attrs);
    } catch {
      return null;
    }
  }
  async signIn(input) {
    const credentials = {
      username: input.email.toLowerCase(),
      password: input.password
    };
    try {
      const result = await signIn(credentials);
      return this.mapSignInStep(result.nextStep);
    } catch (e) {
      if (!isLingeringSessionError(e)) throw mapAmplifyError(e);
      await this.signOut().catch(() => void 0);
      const result = await tryAmplify(() => signIn(credentials));
      return this.mapSignInStep(result.nextStep);
    }
  }
  async confirmSignIn(input) {
    const result = await tryAmplify(
      () => confirmSignIn({
        challengeResponse: input.challengeResponse
      })
    );
    return this.mapSignInStep(result.nextStep);
  }
  async signUp(input) {
    const userAttributes = {
      email: input.email.toLowerCase(),
      given_name: input.firstName,
      family_name: input.lastName
    };
    if (input.metadata) {
      for (const [key, value] of Object.entries(input.metadata)) {
        userAttributes[`custom:${key}`] = value;
      }
    }
    const result = await tryAmplify(
      () => signUp({
        username: input.email.toLowerCase(),
        password: input.password,
        options: { userAttributes }
      })
    );
    if (result.nextStep.signUpStep === "DONE") {
      return { kind: "COMPLETE" };
    }
    return { kind: "CONFIRMATION_REQUIRED", method: "CODE" };
  }
  async confirmSignUp(input) {
    await tryAmplify(
      () => confirmSignUp({
        username: input.email.toLowerCase(),
        confirmationCode: input.code
      })
    );
  }
  async resendSignUpCode(input) {
    await tryAmplify(
      () => resendSignUpCode({
        username: input.email.toLowerCase()
      })
    );
  }
  async forgotPassword(input) {
    await tryAmplify(() => resetPassword({ username: input.email.toLowerCase() }));
  }
  async confirmForgotPassword(input) {
    await tryAmplify(
      () => confirmResetPassword({
        username: input.email.toLowerCase(),
        confirmationCode: input.code,
        newPassword: input.newPassword
      })
    );
  }
  async changePassword(input) {
    await tryAmplify(
      () => updatePassword({
        oldPassword: input.oldPassword,
        newPassword: input.newPassword
      })
    );
  }
  async signOut() {
    this.cachedMemberships = null;
    await tryAmplify(() => signOut({ global: true }));
  }
  async getIdToken() {
    const { tokens } = await tryAmplify(() => fetchAuthSession());
    return tokens?.idToken?.toString() ?? null;
  }
  // ------------------------------------------------------------------------
  // Multi-tenancy. The Cognito reference implementation:
  //   - active tenant lives in the `active_tenant` JWT claim (Pre-Token
  //     Generation Lambda);
  //   - switching POSTs the developer API + force-refreshes the session so
  //     the next request carries the updated claim;
  //   - memberships come from the developer-API memberships endpoint.
  // ------------------------------------------------------------------------
  async getMemberships() {
    const idToken = await this.getIdToken();
    if (!idToken) return [];
    const resp = await fetch(`${this.config.developerApiBase}/developer/memberships`, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
    if (!resp.ok) {
      if (resp.status === 401 || resp.status === 403 || resp.status === 404) return [];
      const body = await resp.text().catch(() => "");
      throw new AuthError("UNKNOWN", `Failed to load memberships: ${resp.status} ${body}`);
    }
    const json = await resp.json().catch(() => null);
    let rows = [];
    if (Array.isArray(json)) {
      rows = json;
    } else if (json && typeof json === "object") {
      const envelope = json.memberships;
      if (Array.isArray(envelope)) rows = envelope;
    }
    return rows.map((r) => this.mapMembership(r));
  }
  async getActiveTenant() {
    const idToken = await this.getIdToken();
    if (!idToken) return null;
    try {
      const claims = decodeJwt(idToken);
      const tenant = claims["active_tenant"];
      return typeof tenant === "string" && tenant.length > 0 ? tenant : null;
    } catch {
      return null;
    }
  }
  async getActivePartnerUserId() {
    const idToken = await this.getIdToken();
    if (!idToken) return null;
    try {
      const claims = decodeJwt(idToken);
      const id = claims["active_partner_user_id"];
      return typeof id === "string" && id.length > 0 ? id : null;
    } catch {
      return null;
    }
  }
  async setActiveTenant(tenantId) {
    const idToken = await this.getIdToken();
    if (!idToken) {
      throw new AuthError("INVALID_CREDENTIALS", "Not authenticated \u2014 cannot switch tenant.");
    }
    const resp = await fetch(`${this.config.developerApiBase}/developer/active-tenant`, {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId })
    });
    if (!resp.ok) {
      const body = await resp.text().catch(() => "");
      throw new AuthError("UNKNOWN", `Failed to switch active tenant: ${resp.status} ${body}`);
    }
    const result = await resp.json().catch(() => ({}));
    if (result.requiresJwtRefresh) {
      await tryAmplify(() => fetchAuthSession({ forceRefresh: true }));
    }
  }
  async checkUserExists(email) {
    const target = email.toLowerCase();
    const current = await this.getCurrentUser();
    if (current) {
      const isMe = current.email.toLowerCase() === target;
      return { exists: isMe, isMe };
    }
    return { exists: false, isMe: false };
  }
  async linkInvitation(inviteToken) {
    const idToken = await this.getIdToken();
    if (!idToken) {
      throw new AuthError(
        "INVALID_CREDENTIALS",
        "Not authenticated \u2014 sign in before linking an invitation."
      );
    }
    const resp = await fetch(`${this.config.developerApiBase}/developer/link-invitation`, {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
      // Backend body field is snake_case `invite_token` (DeveloperLinkInvitationHandler).
      body: JSON.stringify({ invite_token: inviteToken })
    });
    if (!resp.ok) {
      const body = await resp.text().catch(() => "");
      throw new AuthError("UNKNOWN", `Failed to link invitation: ${resp.status} ${body}`);
    }
    const data = await resp.json();
    return {
      tenantId: data.tenantId,
      partnerUserId: data.partnerUserId,
      role: data.role ?? "SUB_USER",
      alreadyActive: data.alreadyActive ?? false
    };
  }
  /**
   * Mint a short-lived partner-API bearer (st_*) scoped to `tenantId`. The
   * Admin App authenticates to the partner API (/v1/*) with THIS, not the
   * Cognito id token. Returns the token + its expiry (epoch ms).
   *
   * NOT part of AuthProviderAdapter: this is the Vectros-specific partner-API
   * bridge (it mints via the developer API's scoped-token endpoint, whose
   * `tenant=live|test` param is derived from the tenant's membership kind).
   * The host app wires it into the token cache via setPartnerApiTokenMinter; a
   * partner fork swaps the provider + wires its own minter. Keeping the
   * /developer/* call HERE (not in the shared cache) keeps the reference apps
   * forkable.
   *
   * `contextId` (optional): when supplied, the mint requests a token whose
   * `context_id` claim is that AppContext (the data-plane context switcher —
   * app.vectros.ai). The server validates the caller's access to it and 404s
   * uniformly otherwise (the per-context mint). Omitted →
   * tenant-default context (admin-app's control-plane behavior). Until the
   * backend per-context mint ships, the param is accepted by the endpoint
   * but resolves to the default context.
   */
  async mintPartnerApiToken(tenantId, contextId) {
    const idToken = await this.getIdToken();
    if (!idToken) {
      throw new AuthError("INVALID_CREDENTIALS", "Not authenticated \u2014 cannot mint a partner-API token.");
    }
    const kind = await this.resolveTenantKind(tenantId);
    const contextParam = contextId ? `&context=${encodeURIComponent(contextId)}` : "";
    const url = `${this.config.developerApiBase}/developer/scoped-token?tenant=${encodeURIComponent(kind)}&ttl=900${contextParam}`;
    const resp = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${idToken}` }
    });
    if (!resp.ok) {
      const body = await resp.text().catch(() => "");
      throw new AuthError("UNKNOWN", `Partner-API token mint failed: ${resp.status} ${body}`);
    }
    const { token, expiresAt } = await resp.json();
    return { token, expiresAtMs: expiresAt * 1e3 };
  }
  /**
   * List the app contexts the OWNER can reach in `tenantId`. Reads the
   * OWNER-gated developer-API enumeration (`GET /developer/app-contexts?tenant=
   * <live|test>`) — NOT the partner data API, whose `listAppContexts` confines a
   * context-scoped token to its own context (so it can't enumerate siblings).
   * The `tenant` kind is derived from the membership, mirroring the scoped-token
   * mint. Paginates fully; returns [] on no-session / not-an-owner (403/404).
   *
   * `options.onlyMine` adds `mine=true`, which narrows the enumeration to the
   * contexts the caller holds an active access profile in (their provisioned
   * contexts) instead of the full tenant set — the data-plane switcher's view.
   */
  async listAppContexts(tenantId, options) {
    const idToken = await this.getIdToken();
    if (!idToken) return [];
    const kind = await this.resolveTenantKind(tenantId);
    const out = [];
    let startFrom;
    for (let page = 0; page < 50; page++) {
      const qs = new URLSearchParams({ tenant: kind, limit: "100" });
      if (options?.onlyMine) qs.set("mine", "true");
      if (startFrom) qs.set("startFrom", startFrom);
      const resp = await fetch(
        `${this.config.developerApiBase}/developer/app-contexts?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      if (!resp.ok) {
        if (resp.status === 401 || resp.status === 403 || resp.status === 404) return out;
        const body = await resp.text().catch(() => "");
        throw new AuthError("UNKNOWN", `Failed to list app contexts: ${resp.status} ${body}`);
      }
      const json = await resp.json().catch(() => null);
      for (const r of json?.data ?? []) {
        if (r.contextId) {
          out.push({
            contextId: r.contextId,
            name: r.name && r.name.length > 0 ? r.name : r.contextId,
            ...r.status ? { status: r.status } : {}
          });
        }
      }
      const next = json?.nextCursor ?? null;
      if (!next) break;
      startFrom = next;
    }
    return out;
  }
  // ------------------------------------------------------------------------
  // Multi-factor auth — TOTP enrollment + management. All Amplify
  // MFA calls live here so the rest of the app sees only the adapter contract.
  // ------------------------------------------------------------------------
  async getMfaStatus() {
    return tryAmplify(async () => {
      const pref = await fetchMFAPreference();
      const enabled = (pref.enabled ?? []).map((m) => mapCognitoMfaType(m)).filter((m) => m !== null);
      const preferred = pref.preferred ? mapCognitoMfaType(pref.preferred) : null;
      return { enabled, preferred };
    });
  }
  async setUpTotp() {
    return tryAmplify(async () => {
      const details = await setUpTOTP();
      const current = await this.getCurrentUser();
      const accountName = current?.email ?? this.config.productName;
      const uri = details.getSetupUri(this.config.productName, accountName);
      return { secret: details.sharedSecret, otpauthUri: uri.toString() };
    });
  }
  async verifyTotpSetup(code) {
    await tryAmplify(async () => {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: "PREFERRED" });
    });
  }
  async disableTotp() {
    await tryAmplify(() => updateMFAPreference({ totp: "DISABLED" }));
  }
  // ------------------------------------------------------------------------
  // Private mapping helpers — keep all Amplify-specific knowledge isolated.
  // ------------------------------------------------------------------------
  /**
   * Resolve a tenantId to its env mode ('live'|'test') for the scoped-token
   * mint. Reads from the memoized memberships, fetching once if absent. Defaults
   * to 'live' if the id isn't found (safer than 'test' for a tenant the user
   * holds an active membership in; a not-found id is a caller bug anyway).
   */
  async resolveTenantKind(tenantId) {
    if (!this.cachedMemberships) {
      this.cachedMemberships = await this.getMemberships();
    }
    const match = this.cachedMemberships.find((m) => m.tenantId === tenantId);
    return match?.tenantKind === "test" ? "test" : "live";
  }
  /** Map a memberships-endpoint row onto our normalized TenantMembership. */
  mapMembership(r) {
    return {
      tenantId: r.tenantId,
      // Fallbacks are defensive only — the backend supplies all fields. Name
      // falls back to the id; kind/role/status to the safe defaults the
      // backend itself uses for legacy/oddity rows.
      tenantName: r.tenantName ?? r.tenantId,
      tenantKind: r.tenantKind ?? "live",
      role: r.role ?? "SUB_USER",
      status: r.status ?? "ACTIVE",
      partnerId: r.partnerId ?? ""
    };
  }
  attributesToUser(attrs) {
    const email = attrs["email"];
    const sub = attrs["sub"];
    if (!email || !sub) return null;
    return {
      sub,
      email,
      firstName: attrs["given_name"] ?? null,
      lastName: attrs["family_name"] ?? null
    };
  }
  /**
   * Maps Amplify's `signInStep` string onto our SignInResult union. Unknown
   * step values throw `AuthError('UNKNOWN', detailed message)` — silent
   * fall-through on a new Amplify version is a real-world security risk
   * (an unrecognized challenge that we treated as "done" would let a half-
   * authenticated session proceed).
   *
   * Using AuthError (rather than a raw Error) keeps the contract consistent:
   * pages dispatch on `error.code`, and the diagnostic message survives in
   * `error.message` for developer debugging.
   */
  mapSignInStep(nextStep) {
    switch (nextStep.signInStep) {
      case "DONE":
        return { kind: "COMPLETE" };
      case "CONFIRM_SIGN_UP":
        return { kind: "CONFIRMATION_REQUIRED" };
      case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        return { kind: "NEW_PASSWORD_REQUIRED" };
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        return { kind: "MFA_REQUIRED", methods: ["TOTP"] };
      case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
        return { kind: "MFA_REQUIRED", methods: ["SMS"] };
      case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
        return { kind: "MFA_REQUIRED", methods: ["EMAIL"] };
      case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION": {
        const methods = (nextStep.allowedMFATypes ?? []).map((m) => mapCognitoMfaType(m)).filter((m) => m !== null);
        return { kind: "MFA_REQUIRED", methods: methods.length > 0 ? methods : ["TOTP", "SMS"] };
      }
      case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP": {
        const details = nextStep.totpSetupDetails;
        if (details) {
          return {
            kind: "TOTP_SETUP_REQUIRED",
            setup: {
              secret: details.sharedSecret,
              otpauthUri: details.getSetupUri(this.config.productName).toString()
            }
          };
        }
        return { kind: "MFA_REQUIRED", methods: ["TOTP"] };
      }
      default:
        throw new AuthError(
          "UNKNOWN",
          `CognitoAuthProvider: unrecognized sign-in step "${nextStep.signInStep}". This may indicate an Amplify version update introduced a new challenge type; map it explicitly in mapSignInStep().`
        );
    }
  }
};
function AuthCard({
  brandName,
  title,
  subtitle,
  children,
  footer
}) {
  return /* @__PURE__ */ jsx(
    Box,
    {
      sx: {
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.default"
      },
      children: /* @__PURE__ */ jsx(
        Container,
        {
          component: "main",
          maxWidth: "xs",
          sx: {
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            py: { xs: 4, sm: 6 }
          },
          children: /* @__PURE__ */ jsxs(Stack, { alignItems: "stretch", spacing: 3, children: [
            /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "h5",
                component: "span",
                sx: { fontWeight: 700, textAlign: "center", display: "block" },
                children: brandName
              }
            ),
            /* @__PURE__ */ jsx(Card, { sx: { width: "100%" }, children: /* @__PURE__ */ jsx(CardContent, { sx: { p: { xs: 3, sm: 4 } }, children: /* @__PURE__ */ jsxs(Stack, { spacing: 3, children: [
              /* @__PURE__ */ jsxs(Box, { children: [
                /* @__PURE__ */ jsx(Typography, { variant: "h5", component: "h1", sx: { fontWeight: 700 }, children: title }),
                subtitle && /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 1 }, children: subtitle })
              ] }),
              children
            ] }) }) }),
            footer && /* @__PURE__ */ jsx(Box, { sx: { textAlign: "center" }, children: footer })
          ] })
        }
      )
    }
  );
}
var PasswordStrengthMeter2 = lazy(() => Promise.resolve().then(() => (init_PasswordStrengthMeter(), PasswordStrengthMeter_exports)));
function PasswordField({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  required,
  disabled,
  error,
  helperText,
  autoComplete,
  showStrengthMeter = false,
  onStrengthChange
}) {
  const intl = useIntl();
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const meterId = `${inputId}-strength`;
  const [visible, setVisible] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const handleToggle = useCallback(() => {
    setVisible((v) => !v);
  }, []);
  const handleKey = useCallback((e) => {
    setCapsLockOn(e.getModifierState("CapsLock"));
  }, []);
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      TextField,
      {
        id: inputId,
        name,
        label,
        type: visible ? "text" : "password",
        autoComplete,
        value,
        onChange,
        onBlur,
        onKeyDown: handleKey,
        onKeyUp: handleKey,
        required,
        disabled,
        error,
        helperText,
        fullWidth: true,
        slotProps: {
          input: {
            endAdornment: /* @__PURE__ */ jsx(InputAdornment, { position: "end", children: /* @__PURE__ */ jsx(
              IconButton,
              {
                "aria-label": intl.formatMessage({
                  id: visible ? "password.visibilityToggleHide" : "password.visibilityToggleShow"
                }),
                "aria-pressed": visible,
                onClick: handleToggle,
                edge: "end",
                disabled,
                size: "small",
                children: visible ? /* @__PURE__ */ jsx(VisibilityOffIcon, {}) : /* @__PURE__ */ jsx(VisibilityIcon, {})
              }
            ) }),
            // Wire aria-describedby when the meter is showing so screen
            // readers announce the strength alongside the input. Avoids
            // the situation where a visually-sighted user sees the meter
            // but an AT user doesn't.
            ...showStrengthMeter && value ? { "aria-describedby": meterId } : {}
          }
        }
      }
    ),
    capsLockOn && /* @__PURE__ */ jsx(FormHelperText, { role: "status", sx: { ml: 1.75, mt: 0.25 }, children: /* @__PURE__ */ jsx(FormattedMessage, { id: "password.capsLockOn" }) }),
    showStrengthMeter && /* @__PURE__ */ jsx(Suspense, { fallback: null, children: /* @__PURE__ */ jsx(
      PasswordStrengthMeter2,
      {
        value,
        id: meterId,
        ...onStrengthChange ? { onStrengthChange } : {}
      }
    ) })
  ] });
}

// src/index.ts
init_PasswordStrengthMeter();
function RequireAuth({
  children,
  redirectTo = "/login"
}) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const intl = useIntl();
  if (loading) {
    return /* @__PURE__ */ jsx(
      Box,
      {
        sx: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh"
        },
        children: /* @__PURE__ */ jsx(CircularProgress, { "aria-label": intl.formatMessage({ id: "layout.loadingSession" }) })
      }
    );
  }
  if (!isAuthenticated) {
    return /* @__PURE__ */ jsx(Navigate, { to: redirectTo, state: { from: location }, replace: true });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function RequireScope({
  action,
  children,
  redirectTo = "/"
}) {
  const gate = useScopeGate();
  if (gate.loading) return /* @__PURE__ */ jsx(Fragment, { children: null });
  if (!gate.can(action)) return /* @__PURE__ */ jsx(Navigate, { to: redirectTo, replace: true });
  return /* @__PURE__ */ jsx(Fragment, { children });
}
var ErrorBoundary = class extends Component {
  state = { hasError: false };
  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("Render error", error, info.componentStack);
  }
  handleReload = () => {
    window.location.reload();
  };
  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return /* @__PURE__ */ jsx(Container, { maxWidth: "sm", sx: { py: 8 }, children: /* @__PURE__ */ jsxs(Stack, { spacing: 3, alignItems: "flex-start", children: [
      /* @__PURE__ */ jsx(Typography, { variant: "h4", component: "h1", children: "Something went wrong." }),
      /* @__PURE__ */ jsxs(Typography, { variant: "body1", color: "text.secondary", children: [
        "An unexpected error occurred. Please reload the page. If the problem continues, contact",
        " ",
        this.props.supportEmail,
        "."
      ] }),
      /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Button, { variant: "contained", onClick: this.handleReload, children: "Reload" }) })
    ] }) });
  }
};
var DRAWER_WIDTH = 220;
var RAIL_BG = "#09090B";
var RAIL_TEXT = "rgba(255, 255, 255, 0.72)";
var RAIL_HOVER_BG = "rgba(255, 255, 255, 0.08)";
var RAIL_ACTIVE_BG = "rgba(255, 255, 255, 0.14)";
var RAIL_DIVIDER = "rgba(255, 255, 255, 0.12)";
function NavList({ navItems, onItemSelect }) {
  const intl = useIntl();
  return (
    // `component="nav"` makes this a navigation landmark (a11y); the aria-label
    // names it for screen readers and lets tests target the nav directly.
    /* @__PURE__ */ jsx(
      List,
      {
        component: "nav",
        "aria-label": intl.formatMessage({ id: "layout.primaryNav" }),
        sx: { px: 1, pt: 1 },
        children: navItems.map((item) => {
          const link = /* @__PURE__ */ jsx(ListItem, { disablePadding: true, children: /* @__PURE__ */ jsxs(
            ListItemButton,
            {
              component: NavLink,
              to: item.to,
              end: item.to === "/",
              onClick: onItemSelect,
              sx: {
                borderRadius: 1,
                mb: 0.5,
                color: RAIL_TEXT,
                "& .MuiListItemIcon-root": { color: "inherit", minWidth: 36 },
                "&:hover": { backgroundColor: RAIL_HOVER_BG, color: "common.white" },
                "&.active": {
                  backgroundColor: RAIL_ACTIVE_BG,
                  color: "common.white",
                  fontWeight: 700
                }
              },
              children: [
                item.icon != null && /* @__PURE__ */ jsx(ListItemIcon, { children: item.icon }),
                /* @__PURE__ */ jsx(
                  ListItemText,
                  {
                    primary: /* @__PURE__ */ jsx(FormattedMessage, { id: item.labelId }),
                    primaryTypographyProps: { fontSize: 14, fontWeight: "inherit" }
                  }
                )
              ]
            }
          ) }, item.to);
          if (item.gateAction === null) return link;
          return /* @__PURE__ */ jsx(ScopeGate, { action: item.gateAction, children: link }, item.to);
        })
      }
    )
  );
}
function SidebarHeader({
  brandName,
  brandLogoSrc,
  brandQualifier
}) {
  return /* @__PURE__ */ jsxs(
    Box,
    {
      component: NavLink,
      to: "/",
      sx: {
        display: "flex",
        alignItems: "center",
        minHeight: 64,
        px: 2.5,
        py: 2,
        textDecoration: "none",
        color: "inherit"
      },
      children: [
        brandLogoSrc ? /* @__PURE__ */ jsx(Box, { component: "img", src: brandLogoSrc, alt: brandName, sx: { height: 26, display: "block" } }) : /* @__PURE__ */ jsx(
          Typography,
          {
            variant: "h6",
            component: "span",
            sx: { fontWeight: 800, letterSpacing: "-0.02em", color: "common.white" },
            children: brandName
          }
        ),
        brandQualifier && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            Box,
            {
              "aria-hidden": true,
              sx: { width: "1px", alignSelf: "stretch", my: 1.25, ml: 0.25, mr: 1, bgcolor: "rgba(255, 255, 255, 0.2)" }
            }
          ),
          /* @__PURE__ */ jsx(
            Typography,
            {
              component: "span",
              sx: {
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255, 255, 255, 0.6)"
              },
              children: brandQualifier
            }
          )
        ] })
      ]
    }
  );
}
function AppLayout({
  brandName,
  brandLogoSrc,
  brandQualifier,
  navItems,
  switcher
}) {
  const { user, signOut } = useAuth();
  const { loading: tenantLoading } = useCurrentTenant();
  const navigate = useNavigate();
  const intl = useIntl();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const userMenuId = useId();
  const userMenuLabel = intl.formatMessage({ id: "layout.userMenuLabel" });
  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };
  const handleSignOut = async () => {
    handleMenuClose();
    await signOut();
    navigate("/login", { replace: true });
  };
  const handleAccount = () => {
    handleMenuClose();
    navigate("/account");
  };
  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen((open) => !open);
  };
  const handleMobileNavItemSelect = () => {
    setMobileDrawerOpen(false);
  };
  const menuOpen = menuAnchor !== null;
  const avatarInitial = (user?.firstName?.[0] ?? user?.email?.[0] ?? "?").toUpperCase();
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const railPaperSx = {
    width: DRAWER_WIDTH,
    boxSizing: "border-box",
    backgroundColor: RAIL_BG,
    color: "common.white",
    borderRight: "none"
  };
  const railContent = (onItemSelect) => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      SidebarHeader,
      {
        brandName,
        ...brandLogoSrc ? { brandLogoSrc } : {},
        ...brandQualifier ? { brandQualifier } : {}
      }
    ),
    /* @__PURE__ */ jsx(Divider, { sx: { borderColor: RAIL_DIVIDER } }),
    /* @__PURE__ */ jsx(NavList, { navItems, ...onItemSelect ? { onItemSelect } : {} })
  ] });
  return /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", minHeight: "100vh", backgroundColor: "background.default" }, children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        component: "a",
        href: "#main-content",
        sx: {
          // Modern visually-hidden pattern — collapsed to a clipped 1px box until
          // focused, then revealed top-left.
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
          textDecoration: "none",
          "&:focus": {
            left: 8,
            top: 8,
            width: "auto",
            height: "auto",
            overflow: "visible",
            clipPath: "none",
            zIndex: (t) => t.zIndex.tooltip + 1,
            backgroundColor: "background.paper",
            color: "text.primary",
            padding: 1,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider"
          }
        },
        children: /* @__PURE__ */ jsx(FormattedMessage, { id: "layout.skipToContent" })
      }
    ),
    /* @__PURE__ */ jsx(
      Drawer,
      {
        variant: "permanent",
        sx: {
          width: DRAWER_WIDTH,
          flexShrink: 0,
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": railPaperSx
        },
        open: true,
        children: railContent()
      }
    ),
    /* @__PURE__ */ jsx(
      Drawer,
      {
        variant: "temporary",
        open: mobileDrawerOpen,
        onClose: handleMobileDrawerToggle,
        ModalProps: { keepMounted: true },
        sx: {
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": railPaperSx
        },
        children: railContent(handleMobileNavItemSelect)
      }
    ),
    /* @__PURE__ */ jsxs(Box, { sx: { flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }, children: [
      /* @__PURE__ */ jsx(
        AppBar,
        {
          position: "static",
          elevation: 0,
          sx: {
            backgroundColor: "background.paper",
            color: "text.primary",
            borderBottom: "1px solid",
            borderColor: "divider"
          },
          children: /* @__PURE__ */ jsxs(Toolbar, { children: [
            /* @__PURE__ */ jsx(
              IconButton,
              {
                color: "inherit",
                edge: "start",
                onClick: handleMobileDrawerToggle,
                "aria-label": intl.formatMessage({ id: "layout.openNav" }),
                sx: { mr: 1, display: { xs: "inline-flex", md: "none" } },
                children: /* @__PURE__ */ jsx(MenuIcon, {})
              }
            ),
            /* @__PURE__ */ jsx(Box, { sx: { flexGrow: 1 } }),
            user && /* @__PURE__ */ jsxs(Fragment, { children: [
              switcher && /* @__PURE__ */ jsx(Box, { sx: { mr: 1 }, children: switcher }),
              /* @__PURE__ */ jsx(
                IconButton,
                {
                  edge: "end",
                  onClick: handleMenuOpen,
                  "aria-label": userMenuLabel,
                  "aria-controls": menuOpen ? userMenuId : void 0,
                  "aria-haspopup": "true",
                  "aria-expanded": menuOpen,
                  children: /* @__PURE__ */ jsx(
                    Avatar,
                    {
                      sx: {
                        width: 32,
                        height: 32,
                        fontSize: 14,
                        backgroundColor: "primary.main",
                        color: "primary.contrastText"
                      },
                      children: avatarInitial
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsxs(
                Menu,
                {
                  id: userMenuId,
                  anchorEl: menuAnchor,
                  open: menuOpen,
                  onClose: handleMenuClose,
                  anchorOrigin: { vertical: "bottom", horizontal: "right" },
                  transformOrigin: { vertical: "top", horizontal: "right" },
                  slotProps: { list: { "aria-label": userMenuLabel } },
                  children: [
                    /* @__PURE__ */ jsxs(Box, { sx: { px: 2, py: 1 }, children: [
                      /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "text.secondary", component: "div", children: /* @__PURE__ */ jsx(FormattedMessage, { id: "layout.signedInAs" }) }),
                      fullName && /* @__PURE__ */ jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: fullName }),
                      /* @__PURE__ */ jsx(
                        Typography,
                        {
                          variant: "body2",
                          color: fullName ? "text.secondary" : "text.primary",
                          children: user.email
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsx(MenuItem, { onClick: handleAccount, children: /* @__PURE__ */ jsx(FormattedMessage, { id: "layout.account" }) }),
                    /* @__PURE__ */ jsx(MenuItem, { onClick: handleSignOut, children: /* @__PURE__ */ jsx(FormattedMessage, { id: "layout.signOut" }) })
                  ]
                }
              )
            ] })
          ] })
        }
      ),
      /* @__PURE__ */ jsx(Box, { component: "main", id: "main-content", sx: { flexGrow: 1 }, children: /* @__PURE__ */ jsx(Container, { maxWidth: "lg", sx: { py: 4 }, children: tenantLoading ? /* @__PURE__ */ jsx(Box, { sx: { display: "flex", justifyContent: "center", py: 8 }, children: /* @__PURE__ */ jsx(CircularProgress, { "aria-label": intl.formatMessage({ id: "layout.loadingSession" }) }) }) : /* @__PURE__ */ jsx(Outlet, {}) }) })
    ] })
  ] });
}
var CODE_LENGTH = 6;
function TotpEnrollmentWizard({
  secret,
  otpauthUri,
  onVerify,
  pending = false,
  error = null,
  onCancel
}) {
  const intl = useIntl();
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2e3);
    } catch {
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length !== CODE_LENGTH || pending) return;
    onVerify(code);
  };
  return /* @__PURE__ */ jsx("form", { onSubmit: handleSubmit, noValidate: true, children: /* @__PURE__ */ jsxs(Stack, { spacing: 2, children: [
    /* @__PURE__ */ jsx(Typography, { variant: "body1", children: /* @__PURE__ */ jsx(FormattedMessage, { id: "mfa.enrollScanInstruction" }) }),
    /* @__PURE__ */ jsx(Box, { sx: { display: "flex", justifyContent: "center", py: 1 }, children: /* @__PURE__ */ jsx(
      QRCodeSVG,
      {
        value: otpauthUri,
        size: 180,
        title: intl.formatMessage({ id: "mfa.qrTitle" })
      }
    ) }),
    /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 0.5 }, children: /* @__PURE__ */ jsx(FormattedMessage, { id: "mfa.enrollManualLabel" }) }),
      /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [
        /* @__PURE__ */ jsx(
          Typography,
          {
            component: "code",
            sx: {
              fontFamily: "monospace",
              fontSize: 14,
              letterSpacing: 1,
              wordBreak: "break-all",
              bgcolor: "action.hover",
              px: 1,
              py: 0.5,
              borderRadius: 1
            },
            children: secret
          }
        ),
        /* @__PURE__ */ jsx(
          Tooltip,
          {
            title: intl.formatMessage({ id: copied ? "mfa.enrollCopied" : "mfa.enrollCopySecret" }),
            children: /* @__PURE__ */ jsx(
              IconButton,
              {
                size: "small",
                onClick: () => void handleCopy(),
                "aria-label": intl.formatMessage({ id: "mfa.enrollCopySecret" }),
                children: copied ? /* @__PURE__ */ jsx(CheckIcon, { fontSize: "small", color: "success" }) : /* @__PURE__ */ jsx(ContentCopyIcon, { fontSize: "small" })
              }
            )
          }
        )
      ] })
    ] }),
    error && /* @__PURE__ */ jsx(Alert, { severity: "error", role: "alert", children: error }),
    /* @__PURE__ */ jsx(
      TextField,
      {
        label: intl.formatMessage({ id: "mfa.enrollCodeLabel" }),
        value: code,
        onChange: (e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH)),
        disabled: pending,
        required: true,
        fullWidth: true,
        autoFocus: true,
        slotProps: {
          htmlInput: {
            inputMode: "numeric",
            pattern: "[0-9]*",
            autoComplete: "one-time-code",
            maxLength: CODE_LENGTH,
            "aria-label": intl.formatMessage({ id: "mfa.enrollCodeLabel" })
          }
        }
      }
    ),
    /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 1, justifyContent: "flex-end", children: [
      onCancel && /* @__PURE__ */ jsx(Button, { onClick: onCancel, disabled: pending, children: /* @__PURE__ */ jsx(FormattedMessage, { id: "mfa.enrollCancel" }) }),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "submit",
          variant: "contained",
          disabled: code.length !== CODE_LENGTH || pending,
          startIcon: pending ? /* @__PURE__ */ jsx(CircularProgress, { size: 16, color: "inherit" }) : void 0,
          children: /* @__PURE__ */ jsx(FormattedMessage, { id: pending ? "mfa.enrollVerifying" : "mfa.enrollVerify" })
        }
      )
    ] })
  ] }) });
}
function LoadingBlock({ label, py = 6, size }) {
  return /* @__PURE__ */ jsx(Box, { sx: { display: "flex", justifyContent: "center", py }, children: /* @__PURE__ */ jsx(CircularProgress, { "aria-label": label, ...size === void 0 ? {} : { size } }) });
}
function SubmitButton({
  pending = false,
  disabled,
  startIcon,
  children,
  ...rest
}) {
  return /* @__PURE__ */ jsx(
    Button,
    {
      ...rest,
      disabled: disabled || pending,
      "aria-busy": pending || void 0,
      startIcon: pending ? /* @__PURE__ */ jsx(CircularProgress, { size: 16, color: "inherit" }) : startIcon,
      children
    }
  );
}
function MetaList({ children }) {
  return /* @__PURE__ */ jsx(Box, { component: "dl", sx: { m: 0 }, children });
}
function MetaRow({ label, children, labelWidth = 160 }) {
  return /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-child": { borderBottom: 0 }
      },
      children: [
        /* @__PURE__ */ jsx(
          Typography,
          {
            component: "dt",
            variant: "caption",
            sx: {
              width: { xs: "auto", sm: labelWidth },
              flexShrink: 0,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontWeight: 600,
              py: { xs: 0, sm: 0.5 }
            },
            children: label
          }
        ),
        /* @__PURE__ */ jsx(Box, { component: "dd", sx: { flexGrow: 1, m: 0, minWidth: 0, wordBreak: "break-word" }, children })
      ]
    }
  );
}
function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onClose,
  pending = false,
  destructive = true,
  confirmDisabled = false,
  error
}) {
  const titleId = useId();
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      open,
      onClose: () => {
        if (!pending) onClose();
      },
      "aria-labelledby": titleId,
      children: [
        /* @__PURE__ */ jsx(DialogTitle, { id: titleId, children: title }),
        /* @__PURE__ */ jsx(DialogContent, { children: /* @__PURE__ */ jsxs(Stack, { spacing: 2, children: [
          error !== void 0 && error !== null && error !== false && /* @__PURE__ */ jsx(Alert, { severity: "error", role: "alert", children: error }),
          /* @__PURE__ */ jsx(DialogContentText, { children: body })
        ] }) }),
        /* @__PURE__ */ jsxs(DialogActions, { children: [
          /* @__PURE__ */ jsx(Button, { onClick: onClose, disabled: pending, children: cancelLabel }),
          /* @__PURE__ */ jsx(
            SubmitButton,
            {
              variant: "contained",
              color: destructive ? "error" : "primary",
              onClick: onConfirm,
              pending,
              disabled: confirmDisabled,
              children: confirmLabel
            }
          )
        ] })
      ]
    }
  );
}
var NON_DEPLOY_VERSIONS = /* @__PURE__ */ new Set(["", "dev", "development"]);
function VersionUpdateBanner({
  currentVersion,
  versionUrl = "/version.json",
  pollIntervalMs = 6e4,
  message = "A new version of this app is available.",
  refreshLabel = "Refresh",
  dismissLabel = "Dismiss"
}) {
  const [status, setStatus] = useState("idle");
  const currentVersionRef = useRef(currentVersion);
  currentVersionRef.current = currentVersion;
  const enabled = !NON_DEPLOY_VERSIONS.has(currentVersion);
  const check = useCallback(async () => {
    try {
      const res = await fetch(versionUrl, { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      const raw = data && typeof data === "object" && "version" in data ? data.version : void 0;
      const deployed = String(raw ?? "");
      if (deployed && deployed !== currentVersionRef.current) {
        setStatus("available");
      }
    } catch {
    }
  }, [versionUrl]);
  useEffect(() => {
    if (!enabled || status !== "idle") return;
    let cancelled = false;
    const runCheck = () => {
      if (!cancelled) void check();
    };
    const intervalId = setInterval(runCheck, pollIntervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") runCheck();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", runCheck);
    runCheck();
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", runCheck);
    };
  }, [enabled, status, pollIntervalMs, check]);
  if (status !== "available") return null;
  return /* @__PURE__ */ jsx(
    Snackbar,
    {
      open: true,
      anchorOrigin: { vertical: "bottom", horizontal: "center" },
      children: /* @__PURE__ */ jsx(
        Alert,
        {
          severity: "info",
          variant: "filled",
          action: /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Button, { color: "inherit", size: "small", onClick: () => window.location.reload(), children: refreshLabel }),
            /* @__PURE__ */ jsx(Button, { color: "inherit", size: "small", onClick: () => setStatus("dismissed"), children: dismissLabel })
          ] }),
          children: message
        }
      )
    }
  );
}
var I18N_DEFAULT_LOCALE = "en";
function detectLocale(messagesByLocale, defaultLocale) {
  if (typeof navigator === "undefined") {
    return defaultLocale;
  }
  const language = navigator.language.split("-")[0] ?? defaultLocale;
  return language in messagesByLocale ? language : defaultLocale;
}
function IntlProvider({
  children,
  messagesByLocale,
  defaultLocale = I18N_DEFAULT_LOCALE,
  locale
}) {
  const activeLocale = locale ?? detectLocale(messagesByLocale, defaultLocale);
  const messages = messagesByLocale[activeLocale] ?? messagesByLocale[defaultLocale] ?? {};
  return /* @__PURE__ */ jsx(IntlProvider$1, { locale: activeLocale, defaultLocale, messages, children });
}

// src/i18n/messages.en.json
var messages_en_default = {
  "layout.account": "Account",
  "layout.loadingSession": "Loading session",
  "layout.navKeys": "Scoped Keys",
  "layout.navLogs": "Logs",
  "layout.navMembers": "Members",
  "layout.navWelcome": "Welcome",
  "layout.openNav": "Open navigation",
  "layout.primaryNav": "Primary",
  "layout.signOut": "Sign out",
  "layout.signedInAs": "Signed in as",
  "layout.skipToContent": "Skip to main content",
  "layout.tenantSwitcherLabel": "Switch tenant",
  "layout.userMenuLabel": "Open user menu",
  "mfa.cardTitle": "Two-factor authentication",
  "mfa.disableCancel": "Cancel",
  "mfa.disableConfirmBody": "You'll no longer be asked for a code at sign-in. You can turn it back on anytime.",
  "mfa.disableConfirmTitle": "Turn off two-factor authentication?",
  "mfa.disabledBody": "Add a one-time code from an authenticator app to your sign-in for an extra layer of security.",
  "mfa.enabledBody": "An authenticator app is protecting your account \u2014 you'll be asked for a one-time code at sign-in.",
  "mfa.enrollCancel": "Cancel",
  "mfa.enrollCodeLabel": "6-digit code",
  "mfa.enrollCopied": "Copied",
  "mfa.enrollCopySecret": "Copy key",
  "mfa.enrollDialogTitle": "Set up authenticator app",
  "mfa.enrollManualLabel": "Can't scan? Enter this key manually:",
  "mfa.enrollScanInstruction": "Scan this QR code with an authenticator app (Google Authenticator, 1Password, Authy, etc.), then enter the 6-digit code it shows.",
  "mfa.enrollVerify": "Verify & enable",
  "mfa.enrollVerifying": "Verifying\u2026",
  "mfa.loadError": "Couldn't load your security settings. Please refresh and try again.",
  "mfa.loading": "Loading security settings",
  "mfa.preparing": "Preparing setup",
  "mfa.qrTitle": "Authenticator setup QR code",
  "mfa.setUpCta": "Set up authenticator app",
  "mfa.statusDisabled": "Not enabled",
  "mfa.statusEnabled": "Enabled",
  "mfa.turnOffCta": "Turn off",
  "password.capsLockOn": "Caps Lock is on",
  "password.strengthFair": "Fair",
  "password.strengthGood": "Good",
  "password.strengthLabel": "Password strength",
  "password.strengthStrong": "Strong",
  "password.strengthVeryWeak": "Very weak",
  "password.strengthWeak": "Weak",
  "password.visibilityToggleHide": "Hide password",
  "password.visibilityToggleShow": "Show password"
};

export { AppLayout, AuthCard, AuthError, AuthProvider, CognitoAuthProvider, ConfirmDialog, CurrentTenantProvider, ErrorBoundary, I18N_DEFAULT_LOCALE, IntlProvider, LoadingBlock, MetaList, MetaRow, PasswordField, PasswordStrengthMeter_default as PasswordStrengthMeter, RequireAuth, RequireScope, ScopeGate, SubmitButton, TotpEnrollmentWizard, VersionUpdateBanner, __resetScopeGateDecodeCacheForTest, __resetVectrosApiTokenCacheForTest, authErrorToMessage, messages_en_default as baseMessagesEn, clearVectrosApiTokenCache, getVectrosApiToken, isAuthError, setPartnerApiTokenMinter, useActiveTenantId, useAuth, useCurrentTenant, useScopeGate };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map